/**
 * lib/money.ts
 * ------------------------------------------------------------------
 * All money arithmetic for the suite lives here so rounding is
 * consistent everywhere. Amounts are stored as numbers representing
 * Pula with 2 decimal places. Every total flows through `round2` to
 * avoid floating-point drift accumulating across line items.
 *
 * VAT is computed VAT-dormant-correct: when the company is not VAT
 * registered, `lineVat` is always 0 regardless of a line's stored
 * treatment. The treatment is still recorded so that, once
 * registered, recomputation is exact and historical intent is kept.
 * ------------------------------------------------------------------
 */
import type {
  DocumentTotals,
  LineItem,
  VatSnapshot,
  VatTreatment,
} from '@/types';

/** Round to 2 decimal places, half-up, guarding float error. */
export function round2(n: number): number {
  // +Number.EPSILON nudges values like 1.005 that are stored as
  // 1.00499999… so they round up as a human expects.
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/**
 * Compute a single line's net, VAT, and total given the active VAT
 * snapshot. When `vat.registered` is false, VAT is always 0.
 */
export function computeLine(
  input: { quantity: number; unitPrice: number; vatTreatment: VatTreatment },
  vat: VatSnapshot,
): { lineNet: number; lineVat: number; lineTotal: number } {
  const lineNet = round2(input.quantity * input.unitPrice);
  let lineVat = 0;
  if (vat.registered && input.vatTreatment === 'standard') {
    lineVat = round2(lineNet * (vat.rate / 100));
  }
  // 'zero' and 'exempt' contribute 0 VAT; 'zero' is still reportable
  // at 0% but the figure is the same.
  const lineTotal = round2(lineNet + lineVat);
  return { lineNet, lineVat, lineTotal };
}

/**
 * Materialise a set of draft line inputs into full LineItems with a
 * stable id each, computing money fields under the given VAT snapshot.
 */
export function buildLineItems(
  inputs: Array<{
    id?: string;
    description: string;
    quantity: number;
    unitPrice: number;
    vatTreatment: VatTreatment;
  }>,
  vat: VatSnapshot,
): LineItem[] {
  return inputs.map((input, idx) => {
    const { lineNet, lineVat, lineTotal } = computeLine(input, vat);
    return {
      id: input.id ?? `line-${idx + 1}`,
      description: input.description,
      quantity: input.quantity,
      unitPrice: input.unitPrice,
      vatTreatment: input.vatTreatment,
      lineNet,
      lineVat,
      lineTotal,
    };
  });
}

/** Sum a set of line items into document totals. */
export function computeTotals(lines: LineItem[]): DocumentTotals {
  const subtotal = round2(lines.reduce((s, l) => s + l.lineNet, 0));
  const vatTotal = round2(lines.reduce((s, l) => s + l.lineVat, 0));
  const total = round2(subtotal + vatTotal);
  return { subtotal, vatTotal, total };
}

/** Format a number as Botswana Pula, e.g. 52000 -> "P 52,000.00". */
export function formatPula(amount: number): string {
  const sign = amount < 0 ? '-' : '';
  const abs = Math.abs(round2(amount));
  const formatted = abs.toLocaleString('en-BW', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${sign}P ${formatted}`;
}
